import React, { useState, useEffect } from 'react';
import {
  Descriptions,
  Table,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Timeline,
  Spin,
  message,
  Tabs,
  Alert,
  Tooltip,
  Button,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  SettingOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table/interface';
import { 
  PlanogramTask, 
  MoveOutDetail,
  MoveInDetail,
  RearrangeDetail,
  NewItemDetail,
  OperationOverview
} from './task';
import { TaskService } from '../../services/taskService';
import ResizableTable from './ResizableTable';
import dayjs from 'dayjs';
import { escape } from 'lodash';

interface TaskDetailsProps {
  task: PlanogramTask;
}

const TaskDetails: React.FC<TaskDetailsProps> = ({ task }) => {
  const { Text } = Typography;

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('moveOut');
  
  // 四种作业类型的数据状态
  const [operationOverview, setOperationOverview] = useState<Record<string, OperationOverview> | null>(null);
  const [moveOutDetails, setMoveOutDetails] = useState<MoveOutDetail[]>([]);
  const [moveInDetails, setMoveInDetails] = useState<MoveInDetail[]>([]);
  const [rearrangeDetails, setRearrangeDetails] = useState<RearrangeDetail[]>([]);
  const [newItemDetails, setNewItemDetails] = useState<NewItemDetail[]>([]);

  useEffect(() => {
    loadOperationData();
  }, [task.taskCode]);

  const loadOperationData = async () => {
    setLoading(true);
    try {
      // 并行加载所有数据
      const [overviewRes, moveOutRes, moveInRes, rearrangeRes, newItemRes] = await Promise.all([
        TaskService.getOperationOverview(task.shop, task.taskCode),
        TaskService.getMoveOutDetails(task.shop, task.taskCode),
        TaskService.getMoveInDetails(task.shop, task.taskCode),
        TaskService.getRearrangeDetails(task.shop, task.taskCode),
        TaskService.getNewItemDetails(task.shop, task.taskCode)
      ]);

      if (overviewRes.success && overviewRes.data) {
        setOperationOverview(overviewRes.data);
      }
      if (moveOutRes.success && moveOutRes.data) {
        setMoveOutDetails(moveOutRes.data);
      }
      if (moveInRes.success && moveInRes.data) {
        setMoveInDetails(moveInRes.data);
      }
      if (rearrangeRes.success && rearrangeRes.data) {
        setRearrangeDetails(rearrangeRes.data);
      }
      if (newItemRes.success && newItemRes.data) {
        setNewItemDetails(newItemRes.data);
      }
    } catch (error) {
      message.error(t('taskManagement.details.messages.networkError'));
    } finally {
      setLoading(false);
    }
  };

  // 手动刷新数据
  const handleRefresh = async () => {
    await loadOperationData();
  };

  // 获取状态颜色
  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      'Completed': 'success',
      'In Progress': 'processing', 
      'Pending': 'default',
      'Exception': 'error',
      // 兼容旧的中文状态
      '完成': 'success',
      '进行中': 'processing',
      '待处理': 'default',
      '异常': 'error'
    };
    return colorMap[status] || 'default';
  };

  // 获取状态的国际化显示文本
  const getStatusDisplayText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Completed': t('taskManagement.details.status.completed'),
      'In Progress': t('taskManagement.details.status.inProgress'), 
      'Pending': t('taskManagement.details.status.pending'),
      'Exception': t('taskManagement.details.status.exception'),
      // 兼容旧的中文状态
      '完成': t('taskManagement.details.status.completed'),
      '进行中': t('taskManagement.details.status.inProgress'),
      '待处理': t('taskManagement.details.status.pending'),
      '异常': t('taskManagement.details.status.exception')
    };
    return statusMap[status] || status;
  };

   const formatDateTime = (timestamp: string): string => {
      if (!timestamp) {
        return '-';
      }
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

  // 下架作业表格列定义
  const moveOutColumns: ColumnsType<MoveOutDetail> = [
    {
      title: t('taskManagement.details.moveOut.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 100,
    },
    {
      title: t('taskManagement.details.moveOut.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('taskManagement.details.moveOut.columns.itemSpec'),
      dataIndex: 'itemSpec',
      key: 'itemSpec',
      width: 80,
    },
    {
      title: t('taskManagement.details.moveOut.columns.currentLayer'),
      dataIndex: 'currentLayer',
      key: 'currentLayer',
      width: 100,
    },
    {
      title: t('taskManagement.details.moveOut.columns.toLocationCode'),
      dataIndex: 'toLocationCode',
      key: 'toLocationCode',
      width: 150,
    },
    {
      title: t('taskManagement.details.moveOut.columns.targetCart'),
      dataIndex: 'targetCart',
      key: 'targetCart',
      width: 130,
    },
    {
      title: t('taskManagement.details.moveOut.columns.plannedQuantity'),
      dataIndex: 'plannedQuantity',
      key: 'plannedQuantity',
      width: 80,
      align: 'center',
    },
    {
      title: t('taskManagement.details.moveOut.columns.actualQuantity'),
      dataIndex: 'actualQuantity',
      key: 'actualQuantity',
      width: 80,
      align: 'center',
    },
    {
      title: t('taskManagement.details.moveOut.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusDisplayText(status)}</Tag>
      ),
    },
    {
      title: t('taskManagement.details.moveOut.columns.executor'),
      dataIndex: 'executor',
      key: 'executor',
      width: 80,
    },
    {
      title: t('taskManagement.details.moveOut.columns.completeTime'),
      dataIndex: 'completeTime',
      key: 'completeTime',
      width: 150,
      render: (timestamp: string) => formatDateTime(timestamp),
    },
  ];

  // 移入作业表格列定义
  const moveInColumns: ColumnsType<MoveInDetail> = [
    {
      title: t('taskManagement.details.moveIn.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 100,
    },
    {
      title: t('taskManagement.details.moveIn.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('taskManagement.details.moveIn.columns.itemSpec'),
      dataIndex: 'itemSpec',
      key: 'itemSpec',
      width: 80,
    },
    {
      title: t('taskManagement.details.moveIn.columns.targetLayer'),
      dataIndex: 'targetLayer',
      key: 'targetLayer',
      width: 100,
    },
    {
      title: t('taskManagement.details.moveIn.columns.displayOrder'),
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'center',
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.moveIn.columns.plannedXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'plannedXYZFacings',
      width: 120,
      align: 'center',
      render: (_, record: MoveInDetail) => (
        <span>
          {record.plannedXFacings}/{record.plannedYFacings}/{record.plannedZFacings}
        </span>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.moveIn.columns.actualXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'actualXYZFacings',
      width: 120,
      align: 'center',
      render: (_, record: MoveInDetail) => {
        const plannedXYZ = `${record.plannedXFacings}/${record.plannedYFacings}/${record.plannedZFacings}`;
        const actualXYZ = `${record.actualXFacings}/${record.actualYFacings}/${record.actualZFacings}`;
        const hasDiscrepancy = record.actualXFacings !== record.plannedXFacings || 
                              record.actualYFacings !== record.plannedYFacings || 
                              record.actualZFacings !== record.plannedZFacings;
        
        return (
          <span style={{ color: hasDiscrepancy ? '#ff4d4f' : 'inherit' }}>
            {actualXYZ}
          </span>
        );
      },
    },
    {
      title: t('taskManagement.details.moveIn.columns.sourceShelf'),
      dataIndex: 'sourceShelf',
      key: 'sourceShelf',
      width: 100,
    },
    {
      title: t('taskManagement.details.moveIn.columns.sourceLayer'),
      dataIndex: 'sourceLayer',
      key: 'sourceLayer',
      width: 100,
    },
     {
      title: t('taskManagement.details.moveIn.columns.fromContainerCode'),
      dataIndex: 'fromContainerCode',
      key: 'fromContainerCode',
      width: 100,
    },
     {
      title: t('taskManagement.details.moveIn.columns.outResponsiblePerson'),
      dataIndex: 'outResponsiblePerson',
      key: 'outResponsiblePerson',
      width: 100,
    },
    {
      title: t('taskManagement.details.moveIn.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusDisplayText(status)}</Tag>
      ),
    },
  ];

  // 调整作业表格列定义
  const rearrangeColumns: ColumnsType<RearrangeDetail> = [
    {
      title: t('taskManagement.details.rearrange.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 100,
    },
    {
      title: t('taskManagement.details.rearrange.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
       width: 120,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('taskManagement.details.rearrange.columns.itemSpec'),
      dataIndex: 'itemSpec',
      key: 'itemSpec',
      width: 120,
    },
    
    {
      title: t('taskManagement.details.rearrange.columns.sourceLocation'),
      dataIndex: 'sourceLocation',
      key: 'sourceLayer',
      width: 100,
    },
     {
      title: t('taskManagement.details.rearrange.columns.targetLocation'),
      dataIndex: 'targetLocation',
      key: 'targetLocation',
      width: 100,
    },
    {
      title: t('taskManagement.details.rearrange.columns.displayOrder'),
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'center',
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.rearrange.columns.preXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'preXYZFacings',
      width: 180,
      align: 'center',
      render: (_, record: RearrangeDetail) => (
        <span>
          {record.preXFacings}/{record.preYFacings}/{record.preZFacings}
        </span>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.rearrange.columns.plannedXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'plannedXYZFacings',
      width: 180,
      align: 'center',
      render: (_, record: RearrangeDetail) => (
        <span>
          {record.plannedXFacings}/{record.plannedYFacings}/{record.plannedZFacings}
        </span>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.rearrange.columns.actualXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'actualXYZFacings',
      width: 180,
      align: 'center',
      render: (_, record: RearrangeDetail) => {
        const plannedXYZ = `${record.plannedXFacings}/${record.plannedYFacings}/${record.plannedZFacings}`;
        const actualXYZ = `${record.currentXFacings}/${record.currentYFacings}/${record.currentZFacings}`;
        const hasDiscrepancy = record.currentXFacings !== record.plannedXFacings || 
                              record.currentYFacings !== record.plannedYFacings || 
                              record.currentZFacings !== record.plannedZFacings;
        
        return (
          <span style={{ color: hasDiscrepancy ? '#ff4d4f' : 'inherit' }}>
            {actualXYZ}
          </span>
        );
      },
    },
    {
      title: t('taskManagement.details.rearrange.columns.adjustmentType'),
      dataIndex: 'adjustmentType',
      key: 'adjustmentType',
      width: 170,
      render: (type: string) => {
        // 根据英文类型映射到国际化键值
        const getAdjustmentTypeKey = (type: string) => {
          const typeMap: Record<string, string> = {
            'Quantity Increase': 'quantityIncrease',
            'Quantity Decrease': 'quantityDecrease', 
            'Position Adjustment': 'positionAdjustment',
            // 兼容旧的中文类型
            '数量增加': 'quantityIncrease',
            '数量减少': 'quantityDecrease', 
            '位置调整': 'positionAdjustment'
          };
          return typeMap[type] || 'positionAdjustment';
        };
        
        const adjustmentTypeKey = getAdjustmentTypeKey(type);
        const displayText = t(`taskManagement.details.adjustmentTypes.${adjustmentTypeKey}`);
          
        const color = adjustmentTypeKey === 'quantityIncrease' ? 'green' : 
                     adjustmentTypeKey === 'quantityDecrease' ? 'orange' : 'blue';
        return <Tag color={color}>{displayText}</Tag>;
      },
    },
    {
      title: t('taskManagement.details.rearrange.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusDisplayText(status)}</Tag>
      ),
    },
  ];

  // 新商品作业表格列定义
  const newItemColumns: ColumnsType<NewItemDetail> = [
    {
      title: t('taskManagement.details.newItem.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 100,
    },
    {
      title: t('taskManagement.details.newItem.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('taskManagement.details.newItem.columns.itemSpec'),
      dataIndex: 'itemSpec',
      key: 'itemSpec',
      width: 80,
    },
    {
      title: t('taskManagement.details.newItem.columns.targetLayer'),
      dataIndex: 'targetLayer',
      key: 'targetLayer',
      width: 100,
    },
    // {
    //   title: t('taskManagement.details.newItem.columns.displayOrder'),
    //   dataIndex: 'displayOrder',
    //   key: 'displayOrder',
    //   width: 80,
    //   align: 'center',
    // },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.newItem.columns.plannedXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'plannedXYZFacings',
      width: 120,
      align: 'center',
      render: (_, record: NewItemDetail) => (
        <span>
          {record.plannedXFacings}/{record.plannedYFacings}/{record.plannedZFacings}
        </span>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: 'nowrap' }}>
          {t('taskManagement.details.newItem.columns.actualXYZFacings')}
          <Tooltip title={t('taskManagement.details.moveIn.columns.xyzFacingsTooltip')}>
            <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'actualXYZFacings',
      width: 120,
      align: 'center',
      render: (_, record: NewItemDetail) => {
        const plannedXYZ = `${record.plannedXFacings}/${record.plannedYFacings}/${record.plannedZFacings}`;
        const actualXYZ = `${record.actualXFacings}/${record.actualYFacings}/${record.actualZFacings}`;
        const hasDiscrepancy = record.actualXFacings !== record.plannedXFacings || 
                              record.actualYFacings !== record.plannedYFacings || 
                              record.actualZFacings !== record.plannedZFacings;
        
        return (
          <span style={{ color: hasDiscrepancy ? '#ff4d4f' : 'inherit' }}>
            {actualXYZ}
          </span>
        );
      },
    },
    // {
    //   title: t('taskManagement.details.newItem.columns.sourceLocation'),
    //   dataIndex: 'sourceLocation',
    //   key: 'sourceLocation',
    //   width: 100,
    // },
    // {
    //   title: t('taskManagement.details.newItem.columns.sourceLayer'),
    //   dataIndex: 'sourceLayer',
    //   key: 'sourceLayer',
    //   width: 100,
    // },
    {
      title: t('taskManagement.details.newItem.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusDisplayText(status)}</Tag>
      ),
    },
  ];

  // 生成时间线数据
  const getTimelineItems = () => {
    const items = [];
    
    // 1. 任务创建
    items.push({
      color: 'blue',
      dot: <UserOutlined />,
      children: (
        <div>
          <div style={{ fontWeight: 500 }}>{t('taskManagement.details.timeline.taskCreated')}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {formatDateTime(task.gmtCreate)} {t('taskManagement.details.timeline.createdBy', { createBy: "SYS" })}
          </div>
        </div>
      ),
    });

    // 2. 处理时间线事件
    if (task.actualStartTime) {
      items.push({
        color: 'orange',
        dot: <ClockCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: 500 }}>{t('taskManagement.details.timeline.executionStarted')}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {formatDateTime(task.actualStartTime)} {task.startWorkerName && t('taskManagement.details.timeline.startedByUser', { startBy: task.startWorkerName })}
            </div>
          </div>
        ),
      });
    }

    if (task.actualEndTime) {
      items.push({
        color: 'green',
        dot: <CheckCircleOutlined />,
        children: (
          <div>
            <div style={{ fontWeight: 500 }}>{t('taskManagement.details.timeline.taskCompleted')}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {formatDateTime(task.actualEndTime)} {task.completeBy && t('taskManagement.details.timeline.completedByUser', { completeBy: task.completeBy })}
            </div>
          </div>
        ),
      });
    }

    return items;
  };

  // 渲染作业概览统计
  const renderOperationOverview = (overview: OperationOverview, title: string) => (
    <div style={{ marginBottom: 16 }}>
      <Alert
        message={`${title}${t('taskManagement.details.overview.overviewSuffix')}`}
        description={
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title={t('taskManagement.details.overview.totalItems')} value={overview.totalItems} suffix={t('taskManagement.details.overview.unitItems')} />
            </Col>
            <Col span={6}>
              <Statistic title={t('taskManagement.details.overview.totalWeight')} value={overview.totalWeight} suffix={t('taskManagement.details.overview.unitWeight')} precision={1} />
            </Col>
            <Col span={6}>
              <Statistic title={t('taskManagement.details.overview.completedItems')} value={overview.completedCount} suffix={`/${overview.totalCount}`} />
            </Col>
            <Col span={6}>
              <Statistic 
                title={t('taskManagement.details.overview.completionRate')} 
                value={overview.totalCount > 0 ? Math.round((overview.completedCount / overview.totalCount) * 100) : 0} 
                suffix={t('taskManagement.details.overview.unitPercentage')} 
              />
            </Col>
          </Row>
        }
        type="info"
        showIcon
      />
    </div>
  );

  return (
    <Spin spinning={loading}>
      <div style={{ textAlign: 'right', marginBottom: 16 }}>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          disabled={loading}
        >
          {t('common.refresh')}
        </Button>
      </div>
      <Row gutter={16}>
        {/* 左侧主要内容区域 */}
        <Col span={18}>
          {/* 基本信息 */}
          <Card title={t('common.basicInfo')} size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={3} size="small">
              <Descriptions.Item label={t('taskManagement.list.columns.taskCode')}>{task.taskCode}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.shop')}>{task.shop}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.search.displayGroup')}>{task.displayGroup}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.search.division')}>
                <Tag color={task.division === 'Dry Food' ? 'blue' : task.division === 'Fresh Food' ? 'green' : 'orange'}>
                  {task.division}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.search.shelf')}>{task.shelf}</Descriptions.Item>
              <Descriptions.Item label={t('common.planogram')}>{task.displayGroup}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.search.planogramPlan')}>{task.planogramPlan}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.planType')}>{task.planType}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.priority')}>
                <Tag color={task.priority === 'High' ? 'red' : task.priority === 'Medium' ? 'orange' : 'green'}>
                  {task.priority}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.status')}>
                <Tag color={
                    task.status === 'In Progress' ? 'blue' :
                    task.status === 'Not Started' ? 'orange' :
                    task.status === 'Completed' ? 'green' :
                    task.status === 'Paused' ? 'orange' : 'default'
                  }>
                  {task.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.planStartDate')}>{formatDateTime(task.planStartDate)}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.planEndDate')}>{formatDateTime(task.planEndDate)}</Descriptions.Item>

              <Descriptions.Item label={t('taskManagement.list.columns.generateTime')}>{formatDateTime(task.generateTime)}</Descriptions.Item>
              <Descriptions.Item label={t('taskManagement.list.columns.completeTime')}>{formatDateTime(task.completeTime) || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.actualStartTime')}>{formatDateTime(task.actualStartTime) || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.startBy')}>{task.startWorkerName || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.actualEndTime')}>{formatDateTime(task.actualEndTime) || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.completeBy')}>{task.completeBy || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 作业详情 */}
          <Card title={t('taskManagement.details.title')} size="small">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'moveOut',
                  label: (
                    <span>
                      <ShoppingCartOutlined />
                      {t('taskManagement.details.tabs.moveOut')} ({moveOutDetails.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {operationOverview?.moveOut && renderOperationOverview(operationOverview.moveOut, t('taskManagement.details.tabs.moveOut'))}
                      <ResizableTable
                        columns={moveOutColumns}
                        dataSource={moveOutDetails}
                        rowKey={(record) => record.id}
                        size="small"
                        scroll={{ x: 1200 }}
                        // pagination={{
                        //   pageSize: 10,
                        //   showSizeChanger: true,
                        //   showQuickJumper: true,
                        //   showTotal: (total, range) => t('common.paginationTotal', { start: range[0], end: range[1], total }),
                        // }}
                      />
                    </div>
                  ),
                },
                {
                  key: 'moveIn',
                  label: (
                    <span>
                      <SwapOutlined />
                      {t('taskManagement.details.tabs.moveIn')} ({moveInDetails.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {operationOverview?.moveIn && renderOperationOverview(operationOverview.moveIn, t('taskManagement.details.tabs.moveIn'))}
                      <ResizableTable
                        columns={moveInColumns}
                        dataSource={moveInDetails}
                        rowKey={(record) => record.id}
                        size="small"
                        scroll={{ x: 1200 }}
                        // pagination={{
                        //   pageSize: 10,
                        //   showSizeChanger: true,
                        //   showQuickJumper: true,
                        //   showTotal: (total, range) => t('common.paginationTotal', { start: range[0], end: range[1], total }),
                        // }}
                      />
                    </div>
                  ),
                },
                {
                  key: 'rearrange',
                  label: (
                    <span>
                      <SettingOutlined />
                      {t('taskManagement.details.tabs.rearrange')} ({rearrangeDetails.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {operationOverview?.rearrange && renderOperationOverview(operationOverview.rearrange, t('taskManagement.details.tabs.rearrange'))}
                      <ResizableTable
                        columns={rearrangeColumns}
                        dataSource={rearrangeDetails}
                        rowKey={(record) => record.id}
                        size="small"
                        scroll={{ x: 1400 }}
                        // pagination={{
                        //   pageSize: 10,
                        //   showSizeChanger: true,
                        //   showQuickJumper: true,
                        //   showTotal: (total, range) => t('common.paginationTotal', { start: range[0], end: range[1], total }),
                        // }}
                      />
                    </div>
                  ),
                },
                {
                  key: 'newItems',
                  label: (
                    <span>
                      <PlusOutlined />
                      {t('taskManagement.details.tabs.newItem')} ({newItemDetails.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {operationOverview?.newItems && renderOperationOverview(operationOverview.newItems, t('taskManagement.details.tabs.newItem'))}
                      <ResizableTable
                        columns={newItemColumns}
                        dataSource={newItemDetails}
                        rowKey={(record) => record.id}
                        size="small"
                        scroll={{ x: 1200 }}
                        // pagination={{
                        //   pageSize: 10,
                        //   showSizeChanger: true,
                        //   showQuickJumper: true,
                        //   showTotal: (total, range) => t('common.paginationTotal', { start: range[0], end: range[1], total }),
                        // }}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        {/* 右侧执行时间线 */}
        <Col span={6}>
          <Card 
            title={t('common.executionTimeline')} 
            size="small" 
            style={{ 
              position: 'sticky', 
              top: 16,
              height: 'fit-content',
              maxHeight: 'calc(100vh - 32px)',
              overflow: 'auto'
            }}
          >
            <Timeline items={getTimelineItems()} />
          </Card>
        </Col>
      </Row>
    </Spin>
  );
};

export default TaskDetails;